import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const T = {
  bg:        '#060d1e',
  surface:   '#0d1a2d',
  surfaceHi: '#132238',
  border:    '#1e3a5f',
  borderHi:  '#2d5a8e',
  text:      '#f0f6ff',
  textSub:   '#8ba5c8',
  textMute:  '#4a6a8a',
  emerald:   '#10b981',
  emeraldDim:'rgba(16,185,129,0.10)',
  blue:      '#3b82f6',
  blueDim:   'rgba(59,130,246,0.10)',
  green:     '#22c55e',
  amber:     '#f59e0b',
  red:       '#ef4444',
  purple:    '#a78bfa',
  teal:      '#2dd4bf',
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtTimeAgo = (d) => {
  if (!d) return '';
  const diff = Math.floor((new Date() - new Date(d)) / 1000);
  if (diff < 3600)  return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return fmtDate(d);
};

const CATEGORY_STYLES = {
  'FDA Decision':  { color: '#ef4444', bg: 'rgba(239,68,68,0.10)',   border: 'rgba(239,68,68,0.2)'   },
  'Trial Results': { color: '#a78bfa', bg: 'rgba(167,139,250,0.10)', border: 'rgba(167,139,250,0.2)' },
  'Deal':          { color: '#22c55e', bg: 'rgba(34,197,94,0.10)',   border: 'rgba(34,197,94,0.2)'   },
  'Earnings':      { color: '#f59e0b', bg: 'rgba(245,158,11,0.10)',  border: 'rgba(245,158,11,0.2)'  },
  'AdCom':         { color: '#3b82f6', bg: 'rgba(59,130,246,0.10)',  border: 'rgba(59,130,246,0.2)'  },
  'Biotech News':  { color: '#38bdf8', bg: 'rgba(56,189,248,0.08)',  border: 'rgba(56,189,248,0.15)' },
  'Industry News': { color: '#8ba5c8', bg: 'rgba(139,165,200,0.08)', border: 'rgba(139,165,200,0.15)'},
  'Pharma':        { color: '#2dd4bf', bg: 'rgba(45,212,191,0.08)',  border: 'rgba(45,212,191,0.15)' },
};

const getCategoryStyle = (cat) => CATEGORY_STYLES[cat] || CATEGORY_STYLES['Biotech News'];

const FILTERS = [
  { id: 'all',           label: 'All News'     },
  { id: 'FDA Decision',  label: 'FDA'          },
  { id: 'Trial Results', label: 'Trials'       },
  { id: 'Deal',          label: 'Deals'        },
  { id: 'Earnings',      label: 'Earnings'     },
  { id: 'Biotech News',  label: 'Biotech'      },
];

export default function NewsFeed({ session, plan }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState('all');
  const [search, setSearch]     = useState('');
  const [expanded, setExpanded] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;

  useEffect(() => {
    fetchArticles();
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  async function fetchArticles() {
    setLoading(true);
    const { data } = await supabase
      .from('news_feed')
      .select('*')
      .eq('is_relevant', true)
      .order('published_at', { ascending: false })
      .limit(200);
    setArticles(data || []);
    setLoading(false);
  }

  const counts = {};
  articles.forEach(a => { counts[a.category] = (counts[a.category] || 0) + 1; });

  const filtered = articles.filter(a => {
    if (filter !== 'all' && a.category !== filter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return (
        a.title?.toLowerCase().includes(q) ||
        a.source?.toLowerCase().includes(q) ||
        a.ai_summary?.toLowerCase().includes(q) ||
        a.category?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, color: T.text, margin: '0 0 6px', letterSpacing: '-0.5px' }}>
          Biotech News Feed
        </h2>
        <p style={{ color: T.textSub, fontSize: isMobile ? 14 : 16, marginTop: 6, lineHeight: 1.6, margin: 0 }}>
          FDA decisions, trial results, and deals — with AI analysis of what each story means for investors. Updated daily.
        </p>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(4,1fr)', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Total Stories',  value: articles.length,          color: T.emerald },
          { label: 'FDA Decisions',  value: counts['FDA Decision']||0, color: T.red     },
          { label: 'Trial Results',  value: counts['Trial Results']||0,color: T.purple  },
          { label: 'Deals & M&A',    value: counts['Deal']||0,         color: T.green   },
        ].map((s, i) => (
          <div key={i} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: isMobile ? '14px 16px' : '18px 22px' }}>
            <div style={{ fontSize: 11, color: T.textMute, fontWeight: 700, marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{s.label}</div>
            <div style={{ fontSize: isMobile ? 26 : 32, fontWeight: 800, color: s.color, lineHeight: 1 }}>{loading ? '—' : s.value}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: T.textMute, fontSize: 15 }}>⌕</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search news, companies, drugs..."
            style={{ width: '100%', padding: '11px 14px 11px 38px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontSize: 15, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
            onFocus={e => e.target.style.borderColor = T.emerald}
            onBlur={e => e.target.style.borderColor = T.border}
          />
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', borderBottom: `1px solid ${T.border}`, paddingBottom: 0 }}>
          {FILTERS.map(f => {
            const catStyle = f.id === 'all' ? { color: T.emerald } : getCategoryStyle(f.id);
            const isActive = filter === f.id;
            const count = f.id === 'all' ? articles.length : (counts[f.id] || 0);
            return (
              <button key={f.id} onClick={() => setFilter(f.id)} style={{
                padding: isMobile ? '9px 12px 8px' : '11px 18px 10px',
                border: 'none', borderBottom: `2px solid ${isActive ? catStyle.color : 'transparent'}`,
                background: 'transparent', color: isActive ? catStyle.color : T.textSub,
                cursor: 'pointer', fontSize: isMobile ? 13 : 14, fontWeight: isActive ? 700 : 400,
                transition: 'all 0.15s', marginBottom: '-1px', whiteSpace: 'nowrap',
              }}>
                {f.label}{count > 0 && <span style={{ fontSize: 11, opacity: 0.6, marginLeft: 4 }}>({count})</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Results count */}
      <div style={{ fontSize: 14, color: T.textMute, marginBottom: 14 }}>
        {filtered.length} {filter === 'all' ? '' : filter} {filtered.length === 1 ? 'story' : 'stories'} · Updated daily from FDA, BioPharma Dive, STAT News, Endpoints
      </div>

      {/* News cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.textMute }}>
          <div style={{ fontSize: 15, marginBottom: 12 }}>Loading news feed...</div>
          <div style={{ width: 80, height: 2, background: T.border, borderRadius: 1, overflow: 'hidden', margin: '0 auto' }}>
            <div style={{ height: '100%', width: '50%', background: T.emerald, animation: 'loadBar 1.4s ease-in-out infinite' }} />
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.textMute }}>
          <div style={{ fontSize: 16, marginBottom: 8 }}>No stories found</div>
          <div style={{ fontSize: 14 }}>Try a different filter or search term.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map((article, i) => {
            const catStyle = getCategoryStyle(article.category);
            const isExp = expanded === (article.id || i);

            return (
              <div key={article.id || i} style={{
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderLeft: `3px solid ${catStyle.color}`,
                borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.borderColor = T.borderHi}
                onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
              >
                {/* Card row */}
                <div style={{ padding: isMobile ? '14px 16px' : '18px 22px', cursor: 'pointer' }}
                  onClick={() => setExpanded(isExp ? null : (article.id || i))}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>

                    {/* Category dot */}
                    <div style={{
                      flexShrink: 0, width: 10, height: 10,
                      borderRadius: '50%', backgroundColor: catStyle.color,
                      marginTop: 6,
                    }} />

                    {/* Content */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, color: catStyle.color, background: catStyle.bg, border: `1px solid ${catStyle.border}`, flexShrink: 0 }}>
                          {article.category}
                        </span>
                        <span style={{ fontSize: 12, color: T.textMute, flexShrink: 0 }}>{article.source}</span>
                        <span style={{ fontSize: 12, color: T.textMute, flexShrink: 0 }}>{fmtTimeAgo(article.published_at)}</span>
                      </div>

                      <h3 style={{ margin: '0 0 6px', fontSize: isMobile ? 15 : 16, fontWeight: 700, color: T.text, lineHeight: 1.45, letterSpacing: '-0.1px' }}>
                        {article.title}
                      </h3>

                      {article.ai_summary && (
                        <p style={{ margin: 0, fontSize: isMobile ? 13 : 14, color: T.textSub, lineHeight: 1.7 }}>
                          {article.ai_summary}
                        </p>
                      )}
                    </div>

                    {/* Toggle */}
                    <div style={{ flexShrink: 0, fontSize: 12, color: T.textMute, marginTop: 4 }}>
                      {isExp ? '▲' : '▾'}
                    </div>
                  </div>
                </div>

                {/* Expanded */}
                {isExp && (
                  <div style={{ padding: '12px 22px 16px', borderTop: `1px solid ${T.border}`, background: T.surfaceHi, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {(article.tags || []).map((tag, ti) => (
                        <span key={ti} style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 100, color: T.textMute, background: T.surface, border: `1px solid ${T.border}` }}>
                          {tag}
                        </span>
                      ))}
                      <span style={{ fontSize: 12, color: T.textMute, padding: '3px 10px' }}>{fmtDate(article.published_at)}</span>
                    </div>
                    {article.url && (
                      <a href={article.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                        style={{ fontSize: 14, fontWeight: 700, color: T.emerald, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 5, padding: '7px 16px', borderRadius: 7, border: `1px solid ${T.emeraldDim}`, background: T.emeraldDim, transition: 'all 0.15s', flexShrink: 0 }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(16,185,129,0.18)'}
                        onMouseLeave={e => e.currentTarget.style.background = T.emeraldDim}>
                        Read Full Story →
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Disclaimer */}
      <div style={{ marginTop: 24, padding: '14px 18px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10 }}>
        <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.7 }}>
          <strong style={{ color: T.text }}>Sources:</strong> FDA Press Releases, BioPharma Dive, STAT News, Endpoints News, FierceBiotech, Pharmaceutical Technology. AI summaries are for informational purposes only. Not financial advice.
        </div>
      </div>

      <style>{`@keyframes loadBar { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }`}</style>
    </div>
  );
}